import {
	Component,
	Input,
	AfterViewInit,
	ElementRef,
	ViewChildren,
	QueryList,
	NgZone,
	OnDestroy,
	signal,
	computed,
	input,
} from '@angular/core';
import { CarouselActivityComponent } from '../carousel-activity/carousel-activity.component';
import { Activity, Lesson, ReportingStudentLessonProgressReport, TakeStatus } from '@mira-avant/mtlibs';
import { NavigationService } from '../../../../../services/navigation.service';
import { AngularModule } from '../../../../../modules/angular.module';
import { JourneyReports } from '../../../../../services/journey-reports';

interface IBreakpoint {
	maxWidth: number;
	firstElementOffset: number;
	spacing: number;
}

const BREAKPOINTS: IBreakpoint[] = [
	{
		maxWidth: 280,
		firstElementOffset: 320,
		spacing: 600,
	},
	{
		maxWidth: 322,
		firstElementOffset: 320,
		spacing: 500,
	},
	{
		maxWidth: 362,
		firstElementOffset: 320,
		spacing: 460,
	},
	{
		maxWidth: 392,
		firstElementOffset: 300,
		spacing: 420,
	},
	{
		maxWidth: 420,
		firstElementOffset: 240,
		spacing: 380,
	},
	{
		maxWidth: 460,
		firstElementOffset: 240,
		spacing: 360,
	},
	{
		maxWidth: 560,
		firstElementOffset: 180,
		spacing: 310,
	},
	{
		maxWidth: 680,
		firstElementOffset: 130,
		spacing: 230,
	},
	{
		maxWidth: 800,
		firstElementOffset: 110,
		spacing: 260,
	},
	{
		maxWidth: 1024,
		firstElementOffset: 90,
		spacing: 170,
	},
	{
		maxWidth: 1200,
		firstElementOffset: 84,
		spacing: 144,
	},
	{
		maxWidth: 1500,
		firstElementOffset: 80,
		spacing: 109,
	},
	{
		maxWidth: 1900,
		firstElementOffset: 52,
		spacing: 91,
	},
	{
		maxWidth: 2200,
		firstElementOffset: 40,
		spacing: 70,
	},
	{
		maxWidth: 2500,
		firstElementOffset: 40,
		spacing: 64,
	},
	{
		maxWidth: 3000,
		firstElementOffset: 30,
		spacing: 58,
	},
	{
		maxWidth: 3400,
		firstElementOffset: 30,
		spacing: 54,
	},
];

@Component({
	selector: 'app-path-activity-selector',
	standalone: true,
	imports: [AngularModule, CarouselActivityComponent],
	templateUrl: './path-activity-selector.component.html',
	styleUrl: './path-activity-selector.component.scss',
})
export class PathActivitySelectorComponent implements AfterViewInit, OnDestroy {
	@Input('lesson') public lesson: Lesson | undefined;

	activities = input<Activity[]>([]);
	report = signal<ReportingStudentLessonProgressReport | undefined>(undefined);

	@ViewChildren('path') pathElements!: QueryList<ElementRef<SVGPathElement>>;
	@ViewChildren('svgPath') svgElements!: QueryList<ElementRef<SVGSVGElement>>;

	private resizeObserver: ResizeObserver;

	constructor(
		private nav: NavigationService,
		private journeyReportsService: JourneyReports,
		private ngZone: NgZone,
		private el: ElementRef
	) {
		this.resizeObserver = new ResizeObserver(() =>
			this.ngZone.run(() => {
				this.positionActivities();
				this.updatePathTruncation();
			})
		);
	}

	async ngOnInit() {
		if (!this.lesson) {
			throw new Error('Lesson is required');
		}

		const matrixParams = await this.nav.matrixParams$.getValue();

		const courseUuid = matrixParams['courseUuid'];
		const sectionUuid = matrixParams['sectionUuid'];
		const locationUuid = matrixParams['locationUuid'];
		const unitUuid = matrixParams['unitUuid'];

		const report = await this.journeyReportsService.getStudentLessonProgress$(
			courseUuid,
			sectionUuid,
			locationUuid,
			unitUuid,
			this.lesson.uuid
		);

		this.report.set(report);
	}

	ngAfterViewInit() {
		this.resizeObserver.observe(this.el.nativeElement);
		this.positionActivities();
		this.updatePathTruncation();
	}

	ngOnDestroy() {
		this.resizeObserver.disconnect();
	}

	isActivityCompleted = (activity: Activity): boolean => {
		return (
			this.report()?.report.some(
				entry => entry.activityUuid === activity.uuid && entry.status === TakeStatus.COMPLETED
			) ?? false
		);
	};

	isActivityStarted = (activity: Activity): boolean => {
		return (
			this.report()?.report.some(
				entry => entry.activityUuid === activity.uuid && entry.status === TakeStatus.IN_PROGRESS
			) ?? false
		);
	};

	selectActivity(activity: Activity): void {
		this.nav.navigateWithMatrix$(['my', 'journey'], { activityUuid: activity.uuid });
	}

	private getVisiblePathElements(): { path: SVGPathElement; svg: SVGSVGElement } | null {
		const svgArray = this.svgElements.toArray();
		const pathArray = this.pathElements.toArray();

		const visibleIndex = svgArray.findIndex(svg => window.getComputedStyle(svg.nativeElement).display === 'block');

		if (visibleIndex === -1) {
			return null;
		}

		return {
			svg: svgArray[visibleIndex].nativeElement,
			path: pathArray[visibleIndex].nativeElement,
		};
	}

	private getBreakpointByWidth(width: number): IBreakpoint | undefined {
		return BREAKPOINTS.find(breakpoint => width < breakpoint.maxWidth);
	}

	private updatePathTruncation() {
		const elements = this.getVisiblePathElements();
		if (!elements) return;

		const { path } = elements;
		const totalLength = path.getTotalLength();
		const breakpoint = this.getBreakpointByWidth(window.innerWidth);

		if (!breakpoint) {
			return;
		}

		const lastActivityIndex = this.activities().length - 1;
		const lengthToLast = breakpoint.firstElementOffset + lastActivityIndex * breakpoint.spacing;

		path.style.strokeDasharray = `${lengthToLast}, ${totalLength}`;
	}

	private positionActivities() {
		const elements = this.getVisiblePathElements();

		if (!elements) {
			console.warn('No visible SVG path found');
			return;
		}

		const { path, svg } = elements;
		const activityElements = this.el.nativeElement.querySelectorAll('app-carousel-activity');
		const totalLength = path.getTotalLength();
		const scale = this.getSvgScale(svg);

		const breakpoint = this.getBreakpointByWidth(window.innerWidth);

		if (!breakpoint) {
			throw new Error('No breakpoint found');
		}

		activityElements.forEach((activity: HTMLElement, index: number) => {
			const lengthAlongPath = breakpoint.firstElementOffset + index * breakpoint.spacing;
			if (lengthAlongPath <= totalLength) {
				const point = path.getPointAtLength(lengthAlongPath);
				activity.style.transform = `translate(${
					point.x * scale.scaleX - activity.offsetWidth / 2
				}px, ${point.y * scale.scaleY - activity.offsetHeight / 2}px)`;
			}
		});

		this.updatePathTruncation();
	}

	private getSvgScale(svg: SVGSVGElement) {
		const svgRect = svg.getBoundingClientRect();
		const viewBox = svg.viewBox.baseVal;
		return {
			scaleX: svgRect.width / viewBox.width,
			scaleY: svgRect.height / viewBox.height,
		};
	}
}
